/* ===========================
   CONCEPT L - THEME JAVASCRIPT
   Lightweight, vanilla JS - no dependencies
   =========================== */

document.addEventListener('DOMContentLoaded', function () {
  // Creator colour injection
  injectCreatorAccents();

  // Product variant selection (if on product page)
  if (window.productData) {
    initProductVariantSelection();
  }

  // Product gallery image swap
  initProductGallery();

  // TRDR-241 — consumer pinch/click zoom on product images
  initProductZoom();

  // Add to cart functionality
  initAddToCart();

  // Mobile menu toggle (if implemented)
  initMobileMenu();

  // Load more (collection pages)
  initLoadMore();

  // Initial cart count update
  updateCartCount();
});

/* ===========================
   CREATOR COLOUR INJECTION
   =========================== */
function injectCreatorAccents() {
  document.querySelectorAll('[data-creator-accent]').forEach(function (el) {
    const accent = el.dataset.creatorAccent;
    if (accent && isValidColor(accent)) {
      el.style.setProperty('--creator-accent', accent);
    }
  });
}

function isValidColor(color) {
  const div = document.createElement('div');
  div.style.color = color;
  return div.style.color !== '';
}

/* ===========================
   PRODUCT VARIANT SELECTION
   Reads all selected option values, finds matching variant,
   updates hidden input, price, main image, and add-to-cart state.
   =========================== */
function initProductVariantSelection() {
  const optionGroups = document.querySelectorAll('.product-option');
  if (!optionGroups.length) return;

  const variantInput = document.querySelector('input[name="id"]');
  const mainImage = document.getElementById('product-main-image');
  const priceEl = document.querySelector('[data-product-price]');
  const addBtn = document.querySelector('.add-to-cart-btn');

  // Track selected value per option index
  const selected = [];
  optionGroups.forEach(function (group) {
    const idx = parseInt(group.dataset.optionIndex, 10);
    const selBtn = group.querySelector('.option-value.selected');
    selected[idx] = selBtn ? selBtn.dataset.optionValue : null;
  });

  function variantOption(v, i) {
    if (Array.isArray(v.options) && v.options.length > i) return v.options[i];
    const key = 'option' + (i + 1);
    return v[key];
  }

  function findVariant() {
    if (!window.productData || !window.productData.variants) return null;
    // Prefer an available variant; only fall back to a sold-out one if that's all there is.
    var variants = window.productData.variants;
    var matching = variants.filter(function (v) {
      return selected.every(function (val, i) {
        if (val == null) return true;
        return variantOption(v, i) === val;
      });
    });
    if (!matching.length) return null;
    var avail = matching.find(function (v) { return v.available; });
    return avail || matching[0];
  }

  // For each option button, check whether any AVAILABLE variant exists that matches
  // the button's value plus everything else currently selected in other option groups.
  // Disable buttons that lead to dead ends so the user can't pick invalid combinations.
  function updateOptionAvailability() {
    if (!window.productData || !window.productData.variants) return;
    var variants = window.productData.variants;
    document.querySelectorAll('.product-option .option-value').forEach(function (btn) {
      var idx = parseInt(btn.dataset.optionIndex, 10);
      var val = btn.dataset.optionValue;

      // Test this button's value against the current selection in OTHER option groups.
      var test = selected.slice();
      test[idx] = val;

      var anyAvailable = variants.some(function (v) {
        if (!v.available) return false;
        return test.every(function (sel, i) {
          if (sel == null) return true;
          return variantOption(v, i) === sel;
        });
      });

      // Any variant at all matching (available or not) — used to decide hide vs disable.
      var anyMatching = variants.some(function (v) {
        return test.every(function (sel, i) {
          if (sel == null) return true;
          return variantOption(v, i) === sel;
        });
      });

      btn.classList.toggle('option-value--sold-out', !anyAvailable && anyMatching);
      btn.classList.toggle('option-value--impossible', !anyMatching);
      btn.dataset.hasAvailable = anyAvailable ? '1' : '0';

      if (anyMatching) {
        btn.removeAttribute('hidden');
      } else {
        // Combo does not exist for this blueprint at all — hide it.
        btn.setAttribute('hidden', '');
      }

      if (!anyAvailable) {
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('title', anyMatching ? 'Sold out' : 'Unavailable');
      } else {
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('title');
      }
    });
  }

  function applyVariant(variant) {
    if (!variant) {
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = 'Unavailable';
      }
      return;
    }

    // Hidden variant ID
    if (variantInput) variantInput.value = variant.id;

    // URL (nice-to-have, doesn't reload)
    try {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    } catch (e) {}

    // Price — TRDR-262 — when variant.compare_at_price is set AND > price,
    // render the original price struck-through + sale price + "N% off"
    // badge. Liquid does the same on first render; this keeps the markup
    // in sync after a variant change. Without this the variant-change
    // handler would wipe the sale visual back to plain price (the bug
    // diagnosed 2026-05-28: server-rendered sale "flashes" on page load
    // then disappears when this handler runs).
    if (priceEl) {
      var _compareAt = Number(variant.compare_at_price) || 0;
      var _price = Number(variant.price) || 0;
      if (_compareAt > _price) {
        var _pct = Math.round(((_compareAt - _price) / _compareAt) * 100);
        priceEl.innerHTML =
          '<s class="product-info-compare-at" style="color:#9ca3af;font-weight:400;font-size:0.75em;margin-right:0.5rem;">'
          + formatPrice(_compareAt) + '</s>'
          + '<span class="product-info-sale-price">' + formatPrice(_price) + '</span>'
          + '<span class="product-info-discount-badge" style="display:inline-block;margin-left:0.5rem;padding:0.15rem 0.5rem;border-radius:0.25rem;background:#be123c;color:#fff;font-size:0.7em;font-weight:600;vertical-align:middle;">'
          + _pct + '% off</span>';
      } else {
        priceEl.textContent = formatPrice(_price);
      }
    }

    // Add-to-cart state
    if (addBtn) {
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? 'Add to bag' : 'Sold out';
    }

    // Main image — if variant has its own featured_image, swap
    if (mainImage && variant.featured_image && variant.featured_image.src) {
      const newSrc = withWidth(variant.featured_image.src, 1200);
      if (newSrc && mainImage.src !== newSrc) {
        mainImage.src = newSrc;
        mainImage.removeAttribute('srcset');
      }
      // Sync thumbnail active state
      syncActiveThumb(variant.featured_image.src);
    }

    // Update colour label (e.g. "Color: Khaki")
    optionGroups.forEach(function (group) {
      const label = group.querySelector('[data-selected-value]');
      if (label) {
        const idx = parseInt(group.dataset.optionIndex, 10);
        if (selected[idx]) label.textContent = selected[idx];
      }
    });
  }

  function withWidth(src, w) {
    if (!src) return src;
    // Shopify CDN URLs — strip existing _123x and inject {width}x0
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', w);
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  function syncActiveThumb(imgSrc) {
    if (!imgSrc) return;
    const base = imgSrc.split('?')[0];
    document.querySelectorAll('.product-gallery-thumb').forEach(function (thumb) {
      const thumbSrc = (thumb.dataset.imageUrl || '').split('?')[0];
      const match = thumbSrc === base || (thumbSrc && base && thumbSrc.indexOf(base) !== -1);
      thumb.classList.toggle('active', match);
    });
  }

  // Click handler for option buttons
  document.querySelectorAll('.product-option .option-value').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      // Block clicks on options that lead to dead ends.
      if (btn.getAttribute('aria-disabled') === 'true' || btn.dataset.hasAvailable === '0') {
        return;
      }
      const idx = parseInt(btn.dataset.optionIndex, 10);
      const val = btn.dataset.optionValue;

      // Deselect siblings in the same option group
      const group = btn.closest('.product-option');
      group.querySelectorAll('.option-value').forEach(function (b) {
        b.classList.remove('selected');
      });
      btn.classList.add('selected');

      selected[idx] = val;
      applyVariant(findVariant());
      // After each pick, recompute which siblings are still reachable.
      updateOptionAvailability();
    });
  });

  // TRDR-256 / TRDR-288 — keep the colour swatch + variant in sync when a
  // shopper clicks a thumbnail in the image strip. The thumb is mapped to
  // its colour via two signals (priority order):
  //   1. URL match against variant.featured_image.src (Printify hero).
  //   2. data-image-alt prefix "trender-colour:<slug>" (Trender multi-
  //      angle restores: back/folded/hanging views tagged at create-time
  //      via the REST POST alt field — see _restore_multi_angle_for_product
  //      in admin.py).
  // Either signal yields the target colour value; we then pick any variant
  // of that colour, switch the swatch, and re-apply the variant. Without
  // signal 2, clicking a back/folded view would only swap the main image
  // and leave the swatch on whatever colour was selected (Paul 2026-05-26
  // — "click on the black back image the colour stays on Navy").
  var colourIdx = null;
  optionGroups.forEach(function (group) {
    var oname = (group.dataset.optionName || '').toLowerCase();
    if (oname.indexOf('color') !== -1 || oname.indexOf('colour') !== -1) {
      colourIdx = parseInt(group.dataset.optionIndex, 10);
    }
  });

  function variantForImage(imgUrl) {
    if (!window.productData || !window.productData.variants || !imgUrl) return null;
    var base = imgUrl.split('?')[0];
    return window.productData.variants.find(function (v) {
      if (!v.featured_image || !v.featured_image.src) return false;
      var vb = v.featured_image.src.split('?')[0];
      return vb === base ||
        (vb && base && (vb.indexOf(base) !== -1 || base.indexOf(vb) !== -1));
    }) || null;
  }

  function colourFromAltTag(altText) {
    if (!altText) return null;
    var s = String(altText).trim().toLowerCase();
    var prefix = 'trender-colour:';
    if (s.indexOf(prefix) !== 0) return null;
    var rest = s.substring(prefix.length).trim();
    if (!rest) return null;
    // New tag format: "trender-colour:{slug}:{angle}" — take everything
    // before the first colon as the colour slug. Old format without
    // angle still works (no colon, whole rest = slug).
    var slug = rest.indexOf(':') >= 0 ? rest.substring(0, rest.indexOf(':')) : rest;
    // Slug uses hyphens for spaces — invert when matching variant option values.
    return slug.replace(/-/g, ' ');
  }

  function variantForColourValue(colourValue) {
    if (!window.productData || !window.productData.variants || !colourValue || colourIdx == null) return null;
    var needle = colourValue.toLowerCase();
    return window.productData.variants.find(function (v) {
      var optKey = 'option' + (colourIdx + 1);
      var val = (v[optKey] || '').toLowerCase();
      return val === needle;
    }) || null;
  }

  if (colourIdx != null) {
    document.querySelectorAll('.product-gallery-thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        // Try URL match first (matches Printify-set variant primaries).
        var v = variantForImage(thumb.dataset.imageUrl);
        // Fallback: alt-tag match (matches Trender multi-angle restores).
        if (!v) {
          var altColour = colourFromAltTag(thumb.dataset.imageAlt);
          if (altColour) v = variantForColourValue(altColour);
        }
        if (!v) return; // no colour signal — leave swatch as-is, just swap image
        var colourVal = variantOption(v, colourIdx);
        if (!colourVal || selected[colourIdx] === colourVal) return;

        var group = null;
        optionGroups.forEach(function (g) {
          if (parseInt(g.dataset.optionIndex, 10) === colourIdx) group = g;
        });
        if (!group) return;

        group.querySelectorAll('.option-value').forEach(function (b) {
          b.classList.toggle(
            'selected',
            (b.dataset.optionValue || '') === colourVal
          );
        });

        selected[colourIdx] = colourVal;
        applyVariant(findVariant());
        updateOptionAvailability();
      });
    });
  }

  // Apply initial state (in case product.selected_or_first_available_variant
  // differs from what first option buttons reflect)
  applyVariant(findVariant());
  updateOptionAvailability();
}

function formatPrice(price) {
  // ``price`` is in cents of the *active market's* currency.
  //
  // window.productData = {{ product | json }} in Liquid serializes the
  // variant.price field already localized to the visitor's market — Shopify
  // Markets does this transparently. So the cents value is already £-cents
  // for UK shoppers, $-cents for US shoppers, etc. We do NOT re-apply
  // Shopify.currency.rate (that would double-convert).
  //
  // Just format with the active currency code so the symbol matches what
  // Liquid initially rendered. This preserves the value the page already
  // showed (no flicker), only the formatting/symbol is owned by us.
  //
  // History: was hardcoded to '£' (broke US storefront, fixed 2026-04-26).
  // First fix multiplied by Shopify.currency.rate which double-converted on
  // UK pages (£22 became £16.24); reverted to no-rate as Markets already
  // localizes the JSON. Verified 2026-04-26.
  const amount = (Number(price) || 0) / 100;
  const currencyCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (e) {
    // Fallback if Intl chokes on an unrecognised currency code
    return currencyCode + ' ' + amount.toFixed(2);
  }
}

/* ===========================
   PRODUCT GALLERY (thumbnails)
   =========================== */
function initProductGallery() {
  const thumbnails = document.querySelectorAll('.product-gallery-thumb');
  const mainImage = document.getElementById('product-main-image');

  if (!mainImage || thumbnails.length === 0) {
    return;
  }

  thumbnails.forEach(function (thumb) {
    thumb.addEventListener('click', function (e) {
      e.preventDefault();
      // TRDR-287 P2 — video thumbs open the lightbox instead of
      // swapping the main image. They never get the .active class so
      // the previously-active image thumb keeps its highlight.
      const mediaType = this.dataset.mediaType;
      if (mediaType === 'video' || mediaType === 'external_video') {
        openProductVideoLightbox(this);
        return;
      }
      thumbnails.forEach(function (t) {
        t.classList.remove('active');
      });
      this.classList.add('active');
      const url = this.dataset.imageUrl;
      if (url) {
        mainImage.src = url;
        mainImage.removeAttribute('srcset');
      }
    });
  });

  // TRDR-287 P2 — close handlers for the video lightbox. Bind once,
  // even if no video thumbs are present, so future product updates
  // that add videos work without a hard reload.
  initProductVideoLightbox();
}

/* ===========================
   PRODUCT VIDEO LIGHTBOX (TRDR-287 P2)
   ===========================
   Lightbox that plays lifestyle video clips uploaded via the creator
   dashboard. Shared element opens on click of any video thumb. Close
   on backdrop click, the X button, or Escape. Pauses + resets the
   <video> on close so reopening starts from frame 0.
   =========================== */
function openProductVideoLightbox(thumb) {
  const lightbox = document.getElementById('product-video-lightbox');
  const player = document.getElementById('product-video-lightbox-player');
  const external = document.getElementById('product-video-lightbox-external');
  if (!lightbox) return;

  const mediaType = thumb.dataset.mediaType;
  if (mediaType === 'video') {
    const url = thumb.dataset.videoUrl;
    if (!url || !player) return;
    if (external) external.hidden = true;
    player.hidden = false;
    player.src = url;
    player.currentTime = 0;
    player.play().catch(function () { /* autoplay blocked is fine */ });
  } else if (mediaType === 'external_video') {
    if (!external) return;
    const externalId = thumb.dataset.externalId || '';
    const host = (thumb.dataset.host || '').toLowerCase();
    let embed = '';
    if (host.indexOf('youtube') !== -1) {
      embed = 'https://www.youtube.com/embed/' + externalId + '?autoplay=1';
    } else if (host.indexOf('vimeo') !== -1) {
      embed = 'https://player.vimeo.com/video/' + externalId + '?autoplay=1';
    }
    if (!embed) return;
    if (player) { player.hidden = true; player.src = ''; }
    external.hidden = false;
    external.innerHTML = '<iframe src="' + embed + '" frameborder="0" '
      + 'allow="autoplay; encrypted-media; fullscreen" allowfullscreen '
      + 'width="100%" height="100%"></iframe>';
  } else {
    return;
  }
  lightbox.hidden = false;
  document.body.classList.add('product-video-lightbox-open');
}

function closeProductVideoLightbox() {
  const lightbox = document.getElementById('product-video-lightbox');
  const player = document.getElementById('product-video-lightbox-player');
  const external = document.getElementById('product-video-lightbox-external');
  if (!lightbox) return;
  lightbox.hidden = true;
  document.body.classList.remove('product-video-lightbox-open');
  if (player) {
    try { player.pause(); } catch (e) {}
    player.removeAttribute('src');
    player.load();
  }
  if (external) {
    external.hidden = true;
    external.innerHTML = '';
  }
}

function initProductVideoLightbox() {
  const lightbox = document.getElementById('product-video-lightbox');
  if (!lightbox || lightbox.dataset.initialized === '1') return;
  lightbox.dataset.initialized = '1';

  // Backdrop click — only when the click hits the overlay itself,
  // not children.
  lightbox.addEventListener('click', function (e) {
    if (e.target === lightbox) closeProductVideoLightbox();
  });

  const closeBtn = lightbox.querySelector('.product-video-lightbox-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeProductVideoLightbox);
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !lightbox.hidden) {
      closeProductVideoLightbox();
    }
  });
}

/* ===========================
   PRODUCT IMAGE ZOOM (TRDR-241)
   ===========================
   Consumer "see the design close up" zoom. Tapping/clicking the main
   product image opens a fullscreen lightbox showing a high-res (2048w)
   version of the CURRENT image. Mobile: pinch to zoom + drag to pan.
   Desktop: click toggles 2.5x zoom centred on the click + drag to pan.
   Close via the × button, backdrop tap, or Escape. Theme-only, no deps. */
function initProductZoom() {
  var mainImage = document.getElementById('product-main-image');
  var galleryMain = document.querySelector('.product-gallery-main');
  if (!mainImage || !galleryMain) return;

  // Force a high-res width on a Shopify CDN image URL (capped at source res).
  function hiRes(src) {
    if (!src) return src;
    try {
      var u = new URL(src, window.location.origin);
      u.searchParams.set('width', '2048');
      return u.toString();
    } catch (e) {
      return src;
    }
  }

  var overlay = null, zoomImg = null;
  var scale = 1, panX = 0, panY = 0;
  var isPanning = false, startX = 0, startY = 0;
  var pinchStartDist = 0, pinchStartScale = 1;

  function touchDist(t) {
    var dx = t[0].clientX - t[1].clientX;
    var dy = t[0].clientY - t[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  function applyTransform() {
    zoomImg.style.transform =
      'translate(' + panX + 'px,' + panY + 'px) scale(' + scale + ')';
    zoomImg.style.cursor = scale > 1 ? 'grab' : 'zoom-in';
  }
  function resetView() { scale = 1; panX = 0; panY = 0; applyTransform(); }

  function close() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    resetView();
  }

  function buildOverlay() {
    overlay = document.createElement('div');
    overlay.className = 'cl-zoom-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Zoomed product image');
    overlay.innerHTML =
      '<button type="button" class="cl-zoom-close" aria-label="Close zoom">&times;</button>' +
      '<img class="cl-zoom-img" alt="" draggable="false">';
    document.body.appendChild(overlay);
    zoomImg = overlay.querySelector('.cl-zoom-img');

    overlay.querySelector('.cl-zoom-close').addEventListener('click', close);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();          // backdrop tap closes
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) close();
    });

    // Desktop: click toggles zoom centred on the click point.
    zoomImg.addEventListener('click', function (e) {
      e.stopPropagation();
      if (scale === 1) {
        scale = 2.5;
        var r = zoomImg.getBoundingClientRect();
        panX = (r.width / 2 - (e.clientX - r.left)) * (scale - 1);
        panY = (r.height / 2 - (e.clientY - r.top)) * (scale - 1);
        applyTransform();
      } else {
        resetView();
      }
    });

    // Desktop drag-pan when zoomed.
    zoomImg.addEventListener('mousedown', function (e) {
      if (scale === 1) return;
      isPanning = true; startX = e.clientX - panX; startY = e.clientY - panY;
      e.preventDefault();
    });
    window.addEventListener('mousemove', function (e) {
      if (!isPanning) return;
      panX = e.clientX - startX; panY = e.clientY - startY; applyTransform();
    });
    window.addEventListener('mouseup', function () { isPanning = false; });

    // Mobile: single-finger pan when zoomed, two-finger pinch to zoom.
    zoomImg.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2) {
        pinchStartDist = touchDist(e.touches);
        pinchStartScale = scale;
      } else if (e.touches.length === 1 && scale > 1) {
        isPanning = true;
        startX = e.touches[0].clientX - panX;
        startY = e.touches[0].clientY - panY;
      }
    }, { passive: true });
    zoomImg.addEventListener('touchmove', function (e) {
      if (e.touches.length === 2) {
        if (pinchStartDist > 0) {
          var d = touchDist(e.touches);
          scale = Math.min(4, Math.max(1, pinchStartScale * (d / pinchStartDist)));
          applyTransform();
        }
        e.preventDefault();
      } else if (isPanning && e.touches.length === 1) {
        panX = e.touches[0].clientX - startX;
        panY = e.touches[0].clientY - startY;
        applyTransform();
        e.preventDefault();
      }
    }, { passive: false });
    zoomImg.addEventListener('touchend', function (e) {
      if (e.touches.length === 0) {
        isPanning = false;
        if (scale <= 1.05) resetView();   // snap back if barely zoomed
      }
    });
  }

  function open() {
    if (!overlay) buildOverlay();
    resetView();
    zoomImg.src = hiRes(mainImage.currentSrc || mainImage.src);
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';   // lock background scroll
  }

  galleryMain.classList.add('is-zoomable');
  mainImage.addEventListener('click', open);
  var hint = galleryMain.querySelector('.product-zoom-hint');
  if (hint) {
    hint.addEventListener('click', function (e) { e.stopPropagation(); open(); });
  }
}

/* ===========================
   ADD TO CART (AJAX)
   =========================== */
function initAddToCart() {
  const addBtn = document.querySelector('.add-to-cart-btn');
  if (!addBtn) return;

  addBtn.addEventListener('click', function (e) {
    e.preventDefault();

    // Guard: never submit when the button is in an unavailable/sold-out state.
    if (addBtn.disabled) return;

    const variantInput = document.querySelector('input[name="id"]');
    const quantityInput = document.querySelector('input[name="quantity"]');

    if (!variantInput || !variantInput.value) {
      alert('Please select all options');
      return;
    }

    // Double-check against productData — selection might have drifted into a sold-out combo.
    if (window.productData && window.productData.variants) {
      const vid = parseInt(variantInput.value, 10);
      const v = window.productData.variants.find(function (x) { return x.id === vid; });
      if (!v || !v.available) {
        addBtn.textContent = 'Sold out';
        addBtn.disabled = true;
        return;
      }
    }

    const variantId = parseInt(variantInput.value, 10);
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    addBtn.disabled = true;
    const originalText = addBtn.textContent;
    addBtn.textContent = 'Adding...';

    addToCart(variantId, quantity)
      .then(function () {
        addBtn.textContent = 'Added to bag!';
        setTimeout(function () {
          addBtn.textContent = originalText;
          addBtn.disabled = false;
        }, 1500);
      })
      .catch(function (error) {
        console.error('Add to cart error:', error);
        alert('Error adding to cart. Please try again.');
        addBtn.textContent = originalText;
        addBtn.disabled = false;
      });
  });
}

function addToCart(variantId, quantity) {
  return fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: quantity }),
  })
    .then(function (response) {
      if (!response.ok) throw new Error('Failed to add to cart');
      return response.json();
    })
    .then(function (data) {
      updateCartCount();
      // TRDR-490 — tag the sale's origin so the order webhook can split
      // creator-site vs marketplace revenue. This is the marketplace
      // storefront, so trender_source=marketplace. cart/update.js MERGES
      // attribute keys, so this never clobbers the promoter ref_* attrs.
      // Fire-and-forget: never block or fail the add-to-cart over a tag.
      try {
        fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { trender_source: 'marketplace' } }),
        }).catch(function () {});
      } catch (e) {}
      return data;
    });
}

/* ===========================
   UPDATE CART COUNT
   =========================== */
function updateCartCount() {
  fetch('/cart.js')
    .then(function (response) {
      if (!response.ok) throw new Error('Failed to fetch cart');
      return response.json();
    })
    .then(function (cart) {
      const cartCountEls = document.querySelectorAll('.cart-count');
      cartCountEls.forEach(function (el) {
        el.textContent = cart.item_count;
      });
    })
    .catch(function (error) {
      console.error('Cart update error:', error);
    });
}

/* ===========================
   MOBILE MENU TOGGLE
   =========================== */
function initMobileMenu() {
  const toggle = document.querySelector('.mobile-menu-toggle');
  const nav = document.querySelector('.header-nav');
  if (!toggle || !nav) return;

  // TRDR — mobile bag-overflow fix (2026-06-04). The CSS hides the
  // inline nav on ≤ 768px and the .open class drops it in as a
  // full-width panel under the header. ARIA state keeps the X-icon
  // animation and screen readers in sync.
  const setOpen = function (open) {
    nav.classList.toggle('open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
  };
  toggle.addEventListener('click', function () {
    setOpen(!nav.classList.contains('open'));
  });
  nav.querySelectorAll('a').forEach(function (link) {
    link.addEventListener('click', function () {
      setOpen(false);
    });
  });
  // Reset state if the viewport grows back past the breakpoint while open.
  const mql = window.matchMedia('(min-width: 769px)');
  const onChange = function (e) {
    if (e.matches) setOpen(false);
  };
  if (mql.addEventListener) mql.addEventListener('change', onChange);
  else if (mql.addListener) mql.addListener(onChange);
}

/* ===========================
   LOAD MORE (append products)
   Fetches the next paginated page, extracts product cards,
   appends them, then updates the button URL or removes it.
   =========================== */
function initLoadMore() {
  const btn = document.querySelector('[data-loadmore-btn]');
  const grid = document.getElementById('collection-product-grid');
  if (!btn || !grid) return;

  const countLabel = document.querySelector('[data-loadmore-count]');

  btn.addEventListener('click', function (e) {
    e.preventDefault();
    const nextUrl = btn.dataset.nextUrl;
    if (!nextUrl) return;

    const originalLabel = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Loading…';

    fetch(nextUrl, { headers: { 'X-Requested-With': 'fetch' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to load next page');
        return res.text();
      })
      .then(function (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract new product cards
        const newItems = doc.querySelectorAll('#collection-product-grid .product-grid-item');
        newItems.forEach(function (item) {
          grid.appendChild(item);
        });

        // Re-apply active filter if one is set
        const activeFilter = document.querySelector('.creator-filter--active');
        if (activeFilter) {
          const target = activeFilter.getAttribute('data-filter');
          grid.querySelectorAll('.product-grid-item').forEach(function (item) {
            const cat = item.getAttribute('data-category') || '';
            item.style.display = target === 'all' || cat === target ? '' : 'none';
          });
        }

        // Update count label
        if (countLabel) {
          const shown = grid.querySelectorAll('.product-grid-item').length;
          const total = parseInt(countLabel.dataset.total, 10);
          countLabel.textContent = 'Showing ' + shown + ' of ' + total;
        }

        // Update button with next next-url, or remove if no more pages
        const nextBtn = doc.querySelector('[data-loadmore-btn]');
        if (nextBtn && nextBtn.dataset.nextUrl) {
          btn.dataset.nextUrl = nextBtn.dataset.nextUrl;
          btn.disabled = false;
          btn.textContent = originalLabel;
        } else {
          btn.remove();
        }
      })
      .catch(function (err) {
        console.error('Load more failed:', err);
        btn.disabled = false;
        btn.textContent = originalLabel;
      });
  });
}
